import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import authService from '../services/authService';
import { Restaurant, RestaurantFilters, LoyaltyPlan, LoyaltyPlanDescriptionDto } from '../types';
import './Pages.css';

const PublicRestaurants: React.FC = () => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<RestaurantFilters>({ city: '', name: '' });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const navigate = useNavigate();

  const isFormattedPlan = (plan: LoyaltyPlan | LoyaltyPlanDescriptionDto | undefined): plan is LoyaltyPlanDescriptionDto => {
    return plan !== undefined && 'formattedDescription' in plan;
  };

  const loadRestaurants = async () => {
    try {
      const params: Record<string, string> = {};
      if (filters.city) params.city = filters.city;
      if (filters.name) params.name = filters.name;
      
      const response = await api.get<Restaurant[]>('/restaurants', { params });
      setRestaurants(response.data);
    } catch (error) {
      console.error('Error loading restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setIsAuthenticated(authService.isAuthenticated());
    loadRestaurants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitPoints = (restaurantId: string) => {
    navigate(`/submit-points?restaurantId=${restaurantId}`);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    loadRestaurants();
  };

  if (loading) return <div className="loading">Carregando restaurantes...</div>;

  return (
    <div className="page">
      <div className="hero-section">
        <h1>Descubra Restaurantes</h1>
        <p>Acumule pontos e ganhe recompensas nos seus restaurantes favoritos</p>
        
        <form className="search-bar" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Nome do restaurante"
            value={filters.name}
            onChange={(e) => setFilters({...filters, name: e.target.value})}
          />
          <input
            type="text"
            placeholder="Cidade"
            value={filters.city}
            onChange={(e) => setFilters({...filters, city: e.target.value})}
          />
          <button type="submit" className="btn-search">
            🔍 Buscar
          </button>
        </form>
      </div>

      <div className="container">
        <div className="restaurants-grid">
          {restaurants.map((restaurant) => (
            <div key={restaurant.restaurantId} className="restaurant-card">
              <div className="restaurant-image">
                {restaurant.logoUrl ? (
                  <img src={restaurant.logoUrl} alt={restaurant.name} />
                ) : (
                  <div className="placeholder-image">🍴</div>
                )}
              </div>
              <div className="restaurant-info">
                <h3>{restaurant.name}</h3>
                {restaurant.description && (
                  <p className="restaurant-description">{restaurant.description}</p>
                )}
                <div className="restaurant-location">
                  {(restaurant.address?.street || restaurant.address?.number) && (
                    <div className="location-line">
                      📍 {[restaurant.address?.street, restaurant.address?.number].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {restaurant.address?.complement && (
                    <div className="location-line">{restaurant.address.complement}</div>
                  )}
                  {(restaurant.address?.neighborhood || restaurant.address?.city) && (
                    <div className="location-line">
                      {[restaurant.address?.neighborhood, restaurant.address?.city, restaurant.address?.state]
                        .filter(Boolean)
                        .join(' - ')}
                    </div>
                  )}
                  {restaurant.address?.zipCode && (
                    <div className="location-line">CEP: {restaurant.address.zipCode}</div>
                  )}
                </div>
                {restaurant.activeLoyaltyPlan && (
                  <div className="loyalty-plan-section">
                    <div className="plan-header">
                      <span className="plan-badge">🎁 Programa de Fidelidade</span>
                    </div>
                    <div className="plan-details">
                      <div className="plan-name">
                        {isFormattedPlan(restaurant.activeLoyaltyPlan)
                          ? restaurant.activeLoyaltyPlan.name
                          : restaurant.activeLoyaltyPlan.name}
                      </div>
                      {isFormattedPlan(restaurant.activeLoyaltyPlan) ? (
                        <div className="plan-formatted-description">
                          <p className="formatted-text">
                            {restaurant.activeLoyaltyPlan.formattedDescription}
                          </p>
                        </div>
                      ) : (
                        <>
                          {restaurant.activeLoyaltyPlan.description && (
                            <p className="plan-description">{restaurant.activeLoyaltyPlan.description}</p>
                          )}
                          <div className="plan-rules">
                            <div className="plan-rule">
                              <span className="rule-label">Tipo:</span>
                              <span className="rule-value">
                                {restaurant.activeLoyaltyPlan.type === 1 ? '💰 Baseado em Pontos' : '📋 Por Uso'}
                              </span>
                            </div>
                            <div className="plan-rule">
                              <span className="rule-label">Pontos necessários:</span>
                              <span className="rule-value">{restaurant.activeLoyaltyPlan.targetPoints} pts</span>
                            </div>
                            {restaurant.activeLoyaltyPlan.minimumValue > 0 && (
                              <div className="plan-rule">
                                <span className="rule-label">Valor mínimo:</span>
                                <span className="rule-value">
                                  R$ {restaurant.activeLoyaltyPlan.minimumValue.toFixed(2)}
                                </span>
                              </div>
                            )}
                            <div className="plan-rule">
                              <span className="rule-label">Recompensa:</span>
                              <span className="rule-value reward-highlight">
                                {restaurant.activeLoyaltyPlan.rewardDescription || 
                                 (restaurant.activeLoyaltyPlan.rewardType === 1 ? 'Desconto em dinheiro' :
                                  restaurant.activeLoyaltyPlan.rewardType === 2 ? 'Prato grátis' :
                                  restaurant.activeLoyaltyPlan.rewardType === 3 ? 'Sobremesa grátis' :
                                  'Recompensa personalizada')}
                                {restaurant.activeLoyaltyPlan.rewardValue && 
                                 restaurant.activeLoyaltyPlan.rewardType === 1 && (
                                  <span> de R$ {restaurant.activeLoyaltyPlan.rewardValue.toFixed(2)}</span>
                                )}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {!restaurant.activeLoyaltyPlan && restaurant.isActive && (
                  <span className="active-badge">✨ Restaurante Ativo</span>
                )}
                {isAuthenticated && (
                  <button
                    className="btn-submit-points"
                    onClick={() => handleSubmitPoints(restaurant.restaurantId)}
                  >
                    📝 Cadastrar Pontos
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {restaurants.length === 0 && (
          <div className="empty-state">
            <h3>Nenhum restaurante encontrado</h3>
            <p>Tente ajustar os filtros de busca</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicRestaurants;

